import type {BlueprintModel} from '@/scripts/model/store';
import {unref, type Ref} from 'vue';
import {BlueprintDecoder, BlueprintEncoder, FileNameHandler} from '@/scripts/model/serializer';
import {extractEncodedBlueprint} from '@/scripts/blueprint-text';
import {convertCoiBlueprint, extractCoiBlueprintString, type CoiCatalog} from '@/scripts/coi-import';
import {buildCoiCatalog} from '@/scripts/coi-catalog';
import {ErrorCollector} from '@/scripts/error-collector';
import {useErrorHandler} from '@/composables';
import type {GameData} from '@/scripts/data';

//Fork addition (Stage J): the in-game blueprint catalog is built only
//on the game-string path below (planner pastes must not pay for it);
//memoised per GameData object at module scope.
let coiCatalogCache: {gameData: GameData; catalog: CoiCatalog} | undefined;
function getCoiCatalog(gameData: GameData): CoiCatalog {
    if(coiCatalogCache?.gameData !== gameData) {
        coiCatalogCache = {gameData, catalog: buildCoiCatalog(gameData)};
    }
    return coiCatalogCache.catalog;
}

//Returns true when the blueprint was loaded. Pasted text is first run
//through the tolerant extractor (scripts/blueprint-text.ts), which pulls
//the encoded token — and the ====Name==== header — out of surrounding
//chat text, blank lines and other noise; text without an encoded marker
//(raw JSON, [code]/``` wrapped saves) falls back to the classic path.
//suppressErrorDialog: callers that render their own inline error (the
//load dialog) pass true so a failed paste doesn't also pop the global
//error dialog; the partial-load warning below always uses it.
export function loadBlueprint(
    gameData: GameData,
    blueprintModel: BlueprintModel,
    encodedBlueprint: string,
    fileName: Ref<string | undefined> | string | undefined,
    suppressErrorDialog = false,
): boolean {
    const {showError} = useErrorHandler();
    const ERROR_TITLE = 'Error loading blueprint';

    //Fork addition (Stage J): in-game Captains of Industry blueprint
    //strings ([BF]<digits>:<base64>) are converted to a planner plan
    //before the planner extractor runs, then re-encoded so they flow
    //through the existing decode + model.load path untouched (anchor
    //sync, partial-load warnings and name handling all keep working).
    //extractCoiBlueprintString returns undefined for planner plans,
    //raw JSON, file loads and anchor preloads, so those keep the
    //existing behaviour byte-for-byte. Its `payload` field is the
    //bare base64 run; the converter expects the full token, so it is
    //reconstructed as `<kind><payload length>:<payload>` (the
    //converter does not enforce the length prefix).
    let coiImportName: string | undefined;
    let coiImportNotes: string[] = [];
    const coiExtracted = extractCoiBlueprintString(encodedBlueprint);
    if(coiExtracted) {
        const coiToken = `${coiExtracted.kind}${coiExtracted.payload.length}:${coiExtracted.payload}`;
        const coiResult = convertCoiBlueprint(coiToken, getCoiCatalog(gameData));
        if(!coiResult.ok) {
            if(!suppressErrorDialog)
                showError(ERROR_TITLE, new Error(coiResult.error));
            return false;
        }
        coiImportName = coiResult.name;
        coiImportNotes = coiResult.notes;
        encodedBlueprint = new BlueprintEncoder(gameData, {blueprintCompress: true}).encode(coiResult.savedBlueprint);
    }

    let blueprintName: string | undefined;
    const extracted = extractEncodedBlueprint(encodedBlueprint, gameData.gameDescription.shortName);
    if(extracted) {
        encodedBlueprint = extracted.encodedBlueprint;
        blueprintName = extracted.blueprintName;
    } else {
        ({blueprintName, encodedBlueprint} = FileNameHandler.decodeBlueprintNameHeader(encodedBlueprint));
    }
    const _fileName = unref(fileName);
    //Fork addition (Stage J): the game blueprint's own name sits
    //below a ==== header name but above the fileName fallback.
    if(!blueprintName && coiImportName) {
        blueprintName = coiImportName;
    }
    if(!blueprintName && _fileName) {
        blueprintName = FileNameHandler.fileNameToBlueprintName(_fileName);
    }

    const errorCollector = new ErrorCollector();
    const decoder = new BlueprintDecoder(gameData, errorCollector);
    const decoded = decoder.decode(encodedBlueprint);
    if(!decoded || errorCollector.haveErrors) {
        if(!suppressErrorDialog)
            showError(ERROR_TITLE, errorCollector);
        return false;
    }
    blueprintModel.load(decoded, errorCollector);
    if(blueprintName) {
        blueprintModel.blueprintName = blueprintName;
    }
    if(errorCollector.haveErrors) {
        showError('Blueprint possibly wasn\'t loaded correctly', errorCollector, true);
    }
    //Fork addition (Stage J): import notes are a success report, not
    //an error — like the partial-load warning above they fire
    //regardless of suppressErrorDialog.
    if(coiImportNotes.length) {
        const notesCollector = new ErrorCollector();
        for(const note of coiImportNotes) {
            notesCollector.collectError(new Error(note));
        }
        showError('Imported from a game blueprint', notesCollector, true);
    }
    return true;
}
